import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLocale } from '../contexts/LocaleContext';
import { API_BASE } from '../config';
import { RichTextField, type MentionableEntity } from './RichTextField';
import { RelatedEntityLink } from './RelatedEntityLink';
import { removeMentionFromHtml } from '../utils/entity-utils';
import { mapMentionableEntitiesFromApiResponse } from '../utils/mentionable-entities';
import { useToast } from '../contexts/ToastContext';
import { InlineEditableText } from './InlineEditableText';
import { AnalyzeButton } from './AnalyzeButton';
import { dispatchMentionableEntitiesChangedForDocument } from '../utils/mentionable-entities-events';
import {
  fetchMentionRenameImpact,
  type MentionRenameImpactSource,
} from '../utils/mention-rename';
import { MentionRenameImpactList } from './MentionRenameImpactList';
import { EditDialogFooter } from './EditDialogFooter';
import { EntityDetailModalLayout } from './EntityDetailModalLayout';
import { ConfirmDialog } from './ConfirmDialog';
import { EntityRelevanceDisplay } from './EntityRelevanceDisplay';
import { Badge } from './ui/badge';
import { LinkedImageResourcesCarousel } from './LinkedImageResourcesCarousel';
import { clampEntityRelevance } from '../utils/entity-relevance';

interface Location {
  id: string;
  name: string;
  description?: string | Record<string, string>;
  narrativeFunction?: string | Record<string, string>;
  physicalCharacteristics?: string | Record<string, string>;
  rulesAndLogic?: string | Record<string, string>;
  historicalCorrection?: string | Record<string, string>;
  improvements?: string | Record<string, string>;
  isManual?: boolean;
  userNotes?: string;
  relevance?: number;
  mainImageResourceId?: string | null;
}

type EditableLocationField =
  | 'description'
  | 'narrativeFunction'
  | 'physicalCharacteristics'
  | 'rulesAndLogic'
  | 'historicalCorrection'
  | 'improvements';

function formatAnalysisPlainText(text: string): string {
  return text.replace(/\\n/g, '\n');
}

function stripHtml(html: string): string {
  if (!html || !html.trim()) return '';
  const div = document.createElement('div');
  div.innerHTML = html;
  return (div.textContent ?? div.innerText ?? '').trim();
}

const pickUserNotes = (loc: unknown): string | undefined => {
  if (typeof loc !== 'object' || loc === null) return undefined;
  const o = loc as Record<string, unknown>;
  const raw = o.userNotes ?? o.user_notes;
  if (typeof raw === 'string') return raw;
  return undefined;
};

const looksLikeHtml = (s: string): boolean => /<[a-z][\s\S]*>/i.test(s);

function fieldToEditableText(value: string | Record<string, string> | undefined): string {
  if (!value) return '';
  if (typeof value === 'string') {
    return formatAnalysisPlainText(looksLikeHtml(value) ? stripHtml(value) : value);
  }
  return Object.entries(value)
    .map(([key, description]) => `${key}: ${description}`)
    .join('\n\n');
}

interface LocationDetailProps {
  documentId?: string;
  projectId?: string;
  locationId?: string;
  onClose?: () => void;
  isModal?: boolean;
  onUpdated?: (location: Location) => void;
  onDeleted?: (locationId: string) => void;
}

export const LocationDetail: React.FC<LocationDetailProps> = ({
  documentId: propDocumentId,
  projectId: propProjectId,
  locationId: propLocationId,
  onClose,
  isModal = false,
  onUpdated,
  onDeleted,
}) => {
  const params = useParams<{ projectId?: string; documentId: string; locationId: string }>();
  const routeDocumentId = params.documentId;
  const routeProjectId = params.projectId;
  const routeLocationId = params.locationId;
  const documentId = propDocumentId || routeDocumentId;
  const projectId = propProjectId || routeProjectId;
  const locationId = propLocationId || routeLocationId;
  const { token } = useAuth();
  const { t } = useLocale();
  const [masterKey] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('masterKey');
  });
  const [shareToken] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('shareToken');
  });
  const [location, setLocation] = useState<Location | null>(null);
  const [allLocations, setAllLocations] = useState<Location[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [savingAnalysisField, setSavingAnalysisField] = useState(false);
  const [mentionableEntities, setMentionableEntities] = useState<MentionableEntity[]>([]);
  const [entityRefs, setEntityRefs] = useState<Array<{
    sourceType: string;
    sourceId: string;
    sourceDocumentId: string | null;
    targetType: string;
    targetId: string;
    targetDocumentId: string | null;
  }>>([]);
  const [entityRefsRefreshTrigger, setEntityRefsRefreshTrigger] = useState(0);
  const { showToast, showErrorToast, showFieldSavedToast } = useToast();
  const [editNotesValue, setEditNotesValue] = useState('');
  const [editingName, setEditingName] = useState(false);
  const [editNameValue, setEditNameValue] = useState('');
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [renameImpactOpen, setRenameImpactOpen] = useState(false);
  const [pendingRenameName, setPendingRenameName] = useState<string | null>(null);
  const [renameImpactSources, setRenameImpactSources] = useState<MentionRenameImpactSource[]>([]);
  const [renameImpactLoading, setRenameImpactLoading] = useState(false);
  const [resolvedProjectId, setResolvedProjectId] = useState<string | null>(projectId ?? null);

  useEffect(() => {
    const authHeaders: Record<string, string> = token && !masterKey ? { Authorization: `Bearer ${token}` } : {};

    const fetchMentionableEntities = async (targetProjectId: string): Promise<void> => {
      try {
        const res = await fetch(`${API_BASE}/api/projects/${targetProjectId}/mentionable-entities`, {
          headers: { ...authHeaders },
        });
        if (res.ok) {
          const data = await res.json();
          setMentionableEntities(mapMentionableEntitiesFromApiResponse(data));
        } else {
          setMentionableEntities([]);
        }
      } catch {
        setMentionableEntities([]);
      }
    };

    const fetchEntityRefs = async (targetProjectId: string): Promise<void> => {
      try {
        const res = await fetch(`${API_BASE}/api/projects/${targetProjectId}/entity-references`, {
          headers: { ...authHeaders },
        });
        if (res.ok) {
          const data = await res.json();
          setEntityRefs(data.references ?? []);
        } else {
          setEntityRefs([]);
        }
      } catch {
        setEntityRefs([]);
      }
    };

    const run = async (): Promise<void> => {
      if (!token && !masterKey) return;

      if (projectId) {
        setResolvedProjectId(projectId);
        fetchMentionableEntities(projectId);
        fetchEntityRefs(projectId);
        return;
      }

      if (!documentId) return;

      const url = masterKey
        ? `${API_BASE}/api/documents/${documentId}?masterKey=${encodeURIComponent(masterKey)}`
        : `${API_BASE}/api/documents/${documentId}`;
      const res = await fetch(url, {
        headers: { ...authHeaders },
      });
      if (!res.ok) return;
      const data = await res.json();
      const docProjectId = data.projectId;
      if (docProjectId) {
        setResolvedProjectId(docProjectId);
        fetchMentionableEntities(docProjectId);
        fetchEntityRefs(docProjectId);
      } else {
        setResolvedProjectId(null);
        setMentionableEntities([]);
        setEntityRefs([]);
      }
    };

    run();
  }, [projectId, documentId, token, masterKey, entityRefsRefreshTrigger]);

  useEffect(() => {
    const fetchLocation = async (): Promise<void> => {
      if ((!token && !masterKey) || !documentId || !locationId) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        const url = masterKey
          ? `${API_BASE}/api/documents/${documentId}/locations?masterKey=${encodeURIComponent(masterKey)}`
          : `${API_BASE}/api/documents/${documentId}/locations`;
        const response = await fetch(url, {
          headers: {
            ...(token && !masterKey ? { Authorization: `Bearer ${token}` } : {}),
          },
        });

        if (!response.ok) {
          throw new Error('Failed to fetch locations');
        }

        const data = await response.json();

        let locationsData: Location[] = [];

        if (Array.isArray(data)) {
          locationsData = data;
        } else if (data.locations) {
          locationsData = data.locations;
        } else if (typeof data === 'object' && data !== null && !Array.isArray(data)) {
          const keys = Object.keys(data);
          if (keys.length > 0 && typeof data[keys[0]] === 'object') {
            locationsData = Object.entries(data).map(([name, loc]: [string, any]) => {
              if (typeof loc === 'object' && loc !== null) {
                return { name, ...loc };
              }
              return { name, description: String(loc) };
            });
          }
        }

        if (!Array.isArray(locationsData)) {
          locationsData = [];
        }

        const normalizeField = (field: any): string | Record<string, string> | undefined => {
          if (!field) return undefined;
          if (typeof field === 'string') return field;
          if (typeof field === 'object' && field !== null && !Array.isArray(field)) return field;
          return String(field);
        };

        locationsData = locationsData.map((loc) => ({
          id: loc.id || `temp-${Math.random().toString(36).substr(2, 9)}`,
          name: String(loc.name || ''),
          description: normalizeField(loc.description),
          narrativeFunction: normalizeField(loc.narrativeFunction),
          physicalCharacteristics: normalizeField(loc.physicalCharacteristics),
          rulesAndLogic: normalizeField(loc.rulesAndLogic),
          historicalCorrection: normalizeField(loc.historicalCorrection),
          improvements: normalizeField(loc.improvements),
          isManual: typeof loc.isManual === 'boolean' ? loc.isManual : undefined,
          userNotes: pickUserNotes(loc),
          relevance: clampEntityRelevance(loc.relevance),
          mainImageResourceId:
            typeof loc.mainImageResourceId === 'string' ? loc.mainImageResourceId : null,
        }));

        setAllLocations(locationsData);

        const foundLocation = locationsData.find(
          (loc) => loc.id === locationId
        );

        if (!foundLocation) {
          setError('Ubicación no encontrada');
        } else {
          setLocation(foundLocation);
          setEditNotesValue(foundLocation.userNotes ?? '');
          setEditNameValue(foundLocation.name);
        }
      } catch (err) {
        console.error('Error fetching location:', err);
        setError('Error al cargar la ubicación');
      } finally {
        setLoading(false);
      }
    };

    fetchLocation();
  }, [token, documentId, locationId, masterKey]);

  useEffect(() => {
    if (location) {
      setEditNotesValue(location.userNotes ?? '');
      setEditNameValue(location.name);
    }
  }, [location?.id]);

  const relatedEntities = useMemo(() => {
    if (!locationId || !documentId) return { linksTo: [], referencedIn: [] };
    const entityLookup = new Map<string, MentionableEntity>();
    for (const e of mentionableEntities) {
      const key = `${e.type}:${e.id}:${e.documentId ?? ''}`;
      entityLookup.set(key, e);
    }
    const linksTo: Array<{ id: string; type: string; label: string; documentId?: string }> = [];
    const referencedIn: Array<{ id: string; type: string; label: string; documentId?: string }> = [];
    for (const ref of entityRefs) {
      if (ref.sourceType === 'location' && ref.sourceId === locationId && ref.sourceDocumentId === documentId) {
        const key = `${ref.targetType}:${ref.targetId}:${ref.targetDocumentId ?? ''}`;
        const entity = entityLookup.get(key);
        if (entity) {
          linksTo.push({ id: ref.targetId, type: ref.targetType, label: entity.label, documentId: ref.targetDocumentId ?? undefined });
        }
      }
      if (ref.targetType === 'location' && ref.targetId === locationId && ref.targetDocumentId === documentId) {
        const key = `${ref.sourceType}:${ref.sourceId}:${ref.sourceDocumentId ?? ''}`;
        const entity = entityLookup.get(key);
        if (entity) {
          referencedIn.push({ id: ref.sourceId, type: ref.sourceType, label: entity.label, documentId: ref.sourceDocumentId ?? undefined });
        }
      }
    }
    const dedupe = (arr: typeof linksTo) => {
      const seen = new Set<string>();
      return arr.filter((x) => {
        const k = `${x.type}:${x.id}:${x.documentId ?? ''}`;
        if (seen.has(k)) return false;
        seen.add(k);
        return true;
      });
    };
    return { linksTo: dedupe(linksTo), referencedIn: dedupe(referencedIn) };
  }, [locationId, documentId, entityRefs, mentionableEntities]);

  const handleRemoveLinksTo = useCallback(
    async (entity: { type: string; id: string; documentId?: string }): Promise<void> => {
      if (!locationId || !documentId) return;
      const newNotes = removeMentionFromHtml(editNotesValue || '', entity);
      const url = masterKey
        ? `${API_BASE}/api/documents/${documentId}/locations/${locationId}?masterKey=${encodeURIComponent(masterKey)}`
        : `${API_BASE}/api/documents/${documentId}/locations/${locationId}`;
      try {
        const response = await fetch(url, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            ...(token && !masterKey ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({ userNotes: newNotes.trim() || undefined }),
        });
        if (!response.ok) throw new Error('Failed to update');
        const data = await response.json();
        setLocation((prev) => (prev ? { ...prev, userNotes: data.location.userNotes } : prev));
        setEditNotesValue(data.location.userNotes ?? '');
        setEntityRefsRefreshTrigger((p) => p + 1);
      } catch {
        showErrorToast(t('Failed to remove entity reference'));
      }
    },
    [locationId, documentId, editNotesValue, masterKey, token, t, showErrorToast]
  );

  const handleRemoveReferencedIn = useCallback(
    async (entity: { type: string; id: string; documentId?: string }): Promise<void> => {
      if (!token || !projectId || !locationId || !documentId) return;
      try {
        const res = await fetch(`${API_BASE}/api/projects/${projectId}/entity-references/remove`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            sourceType: entity.type,
            sourceId: entity.id,
            sourceDocumentId: entity.documentId ?? null,
            targetType: 'location',
            targetId: locationId,
            targetDocumentId: documentId,
          }),
        });
        if (res.ok) {
          setEntityRefsRefreshTrigger((p) => p + 1);
        } else {
          showErrorToast(t('Failed to remove entity reference'));
        }
      } catch {
        showErrorToast(t('Failed to remove entity reference'));
      }
    },
    [locationId, documentId, projectId, token, t, showErrorToast]
  );

  const saveLocation = useCallback(async (): Promise<void> => {
    if (!locationId || !location || saving) return;
    const name = editNameValue.trim() || location.name;
    const userNotes = (editNotesValue ?? '').trim() || undefined;
    const hasChanges =
      name !== location.name ||
      (userNotes ?? '') !== (location.userNotes ?? '');
    if (!hasChanges) {
      setEditingName(false);
      if (isModal) {
        onClose?.();
      }
      return;
    }
    try {
      setSaving(true);
      const url = masterKey
        ? `${API_BASE}/api/documents/${documentId}/locations/${locationId}?masterKey=${encodeURIComponent(masterKey)}`
        : `${API_BASE}/api/documents/${documentId}/locations/${locationId}`;
      const response = await fetch(url, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...(token && !masterKey ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ name, userNotes }),
      });
      if (!response.ok) throw new Error('Failed to update location');
      const data = await response.json();
      const savedLocation = data.location as Location;
      setLocation((prev) =>
        prev
          ? { ...prev, name: savedLocation.name, userNotes: savedLocation.userNotes }
          : prev
      );
      setEditNameValue(savedLocation.name);
      setEditingName(false);
      onUpdated?.({
        ...location,
        name: savedLocation.name,
        userNotes: savedLocation.userNotes,
      });
      void dispatchMentionableEntitiesChangedForDocument(
        documentId,
        token ?? null,
        masterKey,
        projectId,
      );
      if (isModal) {
        onClose?.();
      }
    } catch (err) {
      console.error('Error saving location:', err);
      setError('Error al guardar');
    } finally {
      setSaving(false);
    }
  }, [locationId, location, editNameValue, editNotesValue, documentId, masterKey, token, saving, isModal, onClose, onUpdated, projectId]);

  const cancelEditing = useCallback((): void => {
    if (location) {
      setEditNotesValue(location.userNotes ?? '');
      setEditNameValue(location.name);
      setEditingName(false);
    }
    if (isModal) {
      onClose?.();
    }
  }, [location, isModal, onClose]);

  const saveLocationName = useCallback(
    async (name: string, rewriteMentions: boolean): Promise<boolean> => {
      if (!locationId || !location || !documentId || saving) {
        return false;
      }
      try {
        setSaving(true);
        const url = masterKey
          ? `${API_BASE}/api/documents/${documentId}/locations/${locationId}?masterKey=${encodeURIComponent(masterKey)}`
          : `${API_BASE}/api/documents/${documentId}/locations/${locationId}`;
        const response = await fetch(url, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            ...(token && !masterKey ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({ name, rewriteMentions }),
        });
        if (!response.ok) {
          throw new Error('Failed to update location');
        }
        const data = await response.json();
        const saved = data.location as Location;
        const savedLocation: Location = {
          ...location,
          name: saved.name,
        };
        setLocation(savedLocation);
        setEditNameValue(savedLocation.name);
        setAllLocations((prev) =>
          prev.map((loc) => (loc.id === locationId ? { ...loc, name: savedLocation.name } : loc)),
        );
        onUpdated?.(savedLocation);
        void dispatchMentionableEntitiesChangedForDocument(
          documentId,
          token ?? null,
          masterKey,
          projectId,
        );
        return true;
      } catch (err) {
        console.error('Error saving location name:', err);
        setEditNameValue(location.name);
        showErrorToast(t('failed_to_create_location'));
        return false;
      } finally {
        setSaving(false);
      }
    },
    [
      locationId,
      location,
      documentId,
      saving,
      masterKey,
      token,
      onUpdated,
      projectId,
      showErrorToast,
      t,
    ],
  );

  const persistLocationName = useCallback(
    async (nextName: string): Promise<boolean> => {
      if (!locationId || !location || !documentId || saving) {
        return false;
      }
      const name = nextName.trim();
      if (!name || name === location.name) {
        return false;
      }
      try {
        const impact = await fetchMentionRenameImpact({
          documentId,
          entityKind: 'locations',
          entityId: locationId,
          token: token ?? null,
          masterKey,
        });
        if (impact.sources.length === 0) {
          return saveLocationName(name, false);
        }
        setPendingRenameName(name);
        setRenameImpactSources(impact.sources);
        setRenameImpactOpen(true);
        setEditNameValue(location.name);
        return false;
      } catch (err) {
        console.error('Error checking location mention rename impact:', err);
        showErrorToast(t('failed_to_load_mention_rename_impact'));
        setEditNameValue(location.name);
        return false;
      }
    },
    [
      locationId,
      location,
      documentId,
      saving,
      token,
      masterKey,
      saveLocationName,
      showErrorToast,
      t,
    ],
  );

  const confirmLocationRenameWithMentions = useCallback(async (): Promise<void> => {
    if (!pendingRenameName) {
      return;
    }
    setRenameImpactLoading(true);
    try {
      const saved = await saveLocationName(pendingRenameName, true);
      if (saved) {
        showFieldSavedToast(t('name'));
        showToast(t('mention_rename_updated'));
      }
    } finally {
      setRenameImpactLoading(false);
      setPendingRenameName(null);
      setRenameImpactSources([]);
    }
  }, [pendingRenameName, saveLocationName, showFieldSavedToast, showToast, t]);

  const deleteLocation = useCallback(async (): Promise<void> => {
    if (!locationId || !documentId || deleting) return;
    try {
      setDeleting(true);
      const url = masterKey
        ? `${API_BASE}/api/documents/${documentId}/locations/${locationId}?masterKey=${encodeURIComponent(masterKey)}`
        : `${API_BASE}/api/documents/${documentId}/locations/${locationId}`;
      const response = await fetch(url, {
        method: 'DELETE',
        headers: {
          ...(token && !masterKey ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
      if (!response.ok) throw new Error('Failed to delete location');
      void dispatchMentionableEntitiesChangedForDocument(
        documentId,
        token ?? null,
        masterKey,
        projectId,
      );
      onDeleted?.(locationId);
      onClose?.();
    } catch (err) {
      console.error('Error deleting location:', err);
      showErrorToast(t('failed_to_delete_location'));
    } finally {
      setDeleting(false);
    }
  }, [locationId, documentId, deleting, masterKey, token, projectId, onDeleted, onClose, t, showErrorToast]);

  const persistLocationMainImage = useCallback(
    async (resourceId: string | null): Promise<boolean> => {
      if (!locationId || !location || !documentId || savingAnalysisField) {
        return false;
      }
      const previousId = location.mainImageResourceId ?? null;
      if (previousId === resourceId) {
        return true;
      }

      const previousLocation = location;
      setLocation({ ...location, mainImageResourceId: resourceId });
      setSavingAnalysisField(true);
      try {
        const url = masterKey
          ? `${API_BASE}/api/documents/${documentId}/locations/${locationId}?masterKey=${encodeURIComponent(masterKey)}`
          : `${API_BASE}/api/documents/${documentId}/locations/${locationId}`;
        const response = await fetch(url, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            ...(token && !masterKey ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({ mainImageResourceId: resourceId }),
        });
        if (!response.ok) {
          throw new Error('Failed to update main image');
        }
        const data = await response.json();
        const savedId =
          typeof data.location?.mainImageResourceId === 'string'
            ? data.location.mainImageResourceId
            : null;
        const saved = { ...(data.location as Location), mainImageResourceId: savedId };
        setLocation((prev) => (prev ? { ...prev, mainImageResourceId: savedId } : prev));
        setAllLocations((prev) =>
          prev.map((entry) =>
            entry.id === locationId ? { ...entry, mainImageResourceId: savedId } : entry,
          ),
        );
        onUpdated?.(saved);
        showToast(
          savedId ? t('location_main_image_set') : t('location_main_image_cleared'),
        );
        return true;
      } catch (err) {
        setLocation(previousLocation);
        console.error('Error updating location main image:', err);
        showErrorToast(t('location_main_image_failed'));
        return false;
      } finally {
        setSavingAnalysisField(false);
      }
    },
    [
      locationId,
      location,
      documentId,
      savingAnalysisField,
      masterKey,
      token,
      onUpdated,
      showToast,
      showErrorToast,
      t,
    ],
  );

  const persistLocationField = useCallback(
    async (field: EditableLocationField, value: string): Promise<boolean> => {
      if (!locationId || !location || !documentId || savingAnalysisField) {
        return false;
      }

      const previousLocation = location;
      const nextLocation: Location = {
        ...location,
        [field]: value,
      };

      if (JSON.stringify(nextLocation[field]) === JSON.stringify(previousLocation[field])) {
        return false;
      }

      setLocation(nextLocation);
      setSavingAnalysisField(true);

      try {
        const url = masterKey
          ? `${API_BASE}/api/documents/${documentId}/locations/${locationId}?masterKey=${encodeURIComponent(masterKey)}`
          : `${API_BASE}/api/documents/${documentId}/locations/${locationId}`;
        const response = await fetch(url, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            ...(token && !masterKey ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({ [field]: value }),
        });

        if (!response.ok) {
          throw new Error('Failed to update location');
        }

        const data = await response.json();
        const saved = data.location as Location;
        setLocation((prev) => (prev ? { ...prev, ...saved } : prev));
        setAllLocations((prev) =>
          prev.map((loc) => (loc.id === locationId ? { ...loc, ...saved } : loc)),
        );
        onUpdated?.(saved);
        return true;
      } catch (err) {
        setLocation(previousLocation);
        console.error('Error updating location field:', err);
        showErrorToast(t('failed_to_create_location'));
        return false;
      } finally {
        setSavingAnalysisField(false);
      }
    },
    [
      locationId,
      location,
      documentId,
      savingAnalysisField,
      masterKey,
      token,
      onUpdated,
      showErrorToast,
      t,
    ],
  );

  const handleAnalyzeLocation = useCallback(async (): Promise<void> => {
    if (analyzing || !locationId || !documentId) return;

    const abortController = new AbortController();
    let timeoutId: ReturnType<typeof setTimeout> | null = null;

    try {
      setAnalyzing(true);
      setError(null);
      const authToken = shareToken || token;
      const url = masterKey
        ? `${API_BASE}/api/documents/${documentId}/locations/${locationId}/analyze?masterKey=${encodeURIComponent(masterKey)}`
        : `${API_BASE}/api/documents/${documentId}/locations/${locationId}/analyze`;

      timeoutId = setTimeout(() => {
        if (!abortController.signal.aborted) {
          abortController.abort();
        }
      }, 120000);

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          ...(authToken && !masterKey ? { Authorization: `Bearer ${authToken}` } : {}),
        },
        signal: abortController.signal,
      });

      if (timeoutId) {
        clearTimeout(timeoutId);
        timeoutId = null;
      }

      if (!response.ok) {
        throw new Error('Failed to analyze location');
      }

      const data = await response.json();
      const updatedLocation = data.location as Location;
      setLocation((prev) =>
        prev
          ? {
              ...prev,
              historicalCorrection: updatedLocation.historicalCorrection,
              improvements: updatedLocation.improvements,
            }
          : prev,
      );
      setAllLocations((prev) =>
        prev.map((loc) =>
          loc.id === locationId
            ? {
                ...loc,
                historicalCorrection: updatedLocation.historicalCorrection,
                improvements: updatedLocation.improvements,
              }
            : loc,
        ),
      );
    } catch (err) {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      if (err instanceof Error && err.name === 'AbortError') {
        setError(t('analysis_timed_out'));
      } else {
        console.error('Error analyzing location:', err);
        setError(t('failed_to_analyze_location'));
      }
    } finally {
      setAnalyzing(false);
    }
  }, [analyzing, locationId, documentId, shareToken, token, masterKey, t]);

  if (loading) {
    return (
      <div style={{ padding: isModal ? '1rem' : '2rem', maxWidth: isModal ? '100%' : '1200px', margin: isModal ? '0' : '0 auto' }}>
        <div style={{ textAlign: 'center' }}>{t('loading')}</div>
      </div>
    );
  }

  if (error || !location) {
    return (
      <div style={{ padding: isModal ? '1rem' : '2rem', maxWidth: isModal ? '100%' : '1200px', margin: isModal ? '0' : '0 auto' }}>
        <div style={{ color: '#ef4444', textAlign: 'center' }}>
          {error || 'Ubicación no encontrada'}
        </div>
      </div>
    );
  }

  const modalHeader = (
    <div className="flex items-center justify-between gap-3">
      <div className="flex min-w-0 flex-1 items-center gap-2" style={{ marginRight: '1rem' }}>
        <div className="min-w-0 flex-1" data-dialog-skip-autofocus>
          <InlineEditableText
            value={editNameValue}
            editing={editingName}
            onEditingChange={setEditingName}
            onChange={setEditNameValue}
            onCommit={persistLocationName}
            savedFieldLabel={t('name')}
            disabled={saving}
            placeholder={location.name}
            as="h2"
            skipDialogAutofocus
            className="text-foreground"
            displayClassName="m-0"
          />
        </div>
        {location.isManual ? (
          <Badge variant="secondary" className="shrink-0 text-[0.625rem] uppercase">
            {t('manual')}
          </Badge>
        ) : null}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexShrink: 0 }}>
        {(location.historicalCorrection || location.improvements) && (
          <span style={{ color: '#10b981', fontSize: '1rem' }}>✓</span>
        )}
        <AnalyzeButton
          onClick={() => {
            void handleAnalyzeLocation();
          }}
          isAnalyzing={analyzing}
          className="mr-0"
          tooltip={t('analyze_location_tooltip')}
          matchLabelWidth={t('reanalyze')}
        >
          {analyzing
            ? t('analyzing')
            : location.historicalCorrection || location.improvements
              ? t('reanalyze')
              : t('analyze')}
        </AnalyzeButton>
      </div>
    </div>
  );

  const detailFooter = locationId ? (
      <EditDialogFooter
        isEditing
        isSaving={saving || deleting}
        onCancel={cancelEditing}
        onSave={() => {
          void saveLocation();
        }}
        onDelete={() => setDeleteConfirmOpen(true)}
        deleteDisabled={deleting}
      />
  ) : null;

  const detailCard = (
        <div
          className={isModal ? 'bg-background text-foreground' : undefined}
          style={{
            ...(isModal ? {} : { backgroundColor: 'var(--card)', border: '1px solid var(--border)', borderRadius: '8px' }),
            padding: '2rem',
          }}
        >
          {!isModal && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                marginBottom: '1.5rem',
              }}
            >
              <h2
                style={{
                  fontSize: '1.5rem',
                  fontWeight: '600',
                  margin: 0,
                  color: 'var(--foreground)',
                }}
              >
                {location.name}
              </h2>
              {location.isManual ? (
                <Badge variant="secondary" className="shrink-0 text-[0.625rem] uppercase">
                  {t('manual')}
                </Badge>
              ) : null}
            </div>
          )}

          {(() => {
            const editTooltip = t('inline_editable_click_to_edit');
            const canInlineEdit = !shareToken;
            const linkedImagesCarousel =
              locationId && documentId && token && (projectId ?? resolvedProjectId) ? (
                <LinkedImageResourcesCarousel
                  projectId={projectId ?? resolvedProjectId ?? ''}
                  entityType="location"
                  entityId={locationId}
                  documentId={documentId}
                  token={token}
                  refreshKey={entityRefsRefreshTrigger}
                  uploadEnabled={canInlineEdit}
                  mainImageResourceId={location.mainImageResourceId}
                  onMainImageChange={canInlineEdit ? persistLocationMainImage : undefined}
                  onLinked={() => setEntityRefsRefreshTrigger((value) => value + 1)}
                />
              ) : null;

            const renderEditableAnalysisField = (
              field: EditableLocationField,
              label: string,
              options?: { highlight?: 'danger' | 'success' },
            ): React.ReactNode => {
              const value = fieldToEditableText(location[field]);
              if (!value && !canInlineEdit) {
                return null;
              }

              const containerStyle: React.CSSProperties =
                options?.highlight === 'danger'
                  ? {
                      marginBottom: '1.5rem',
                      padding: '1rem',
                      backgroundColor: 'hsl(var(--destructive) / 0.12)',
                      border: '1px solid hsl(var(--destructive) / 0.4)',
                      borderRadius: '6px',
                    }
                  : options?.highlight === 'success'
                    ? {
                        marginBottom: '1.5rem',
                        padding: '1rem',
                        backgroundColor: 'hsl(142 76% 36% / 0.08)',
                        border: '1px solid hsl(142 76% 36% / 0.35)',
                        borderRadius: '6px',
                      }
                    : { marginBottom: '1.5rem' };

              const labelColor =
                options?.highlight === 'danger'
                  ? 'hsl(var(--destructive))'
                  : options?.highlight === 'success'
                    ? 'hsl(142 76% 36%)'
                    : 'var(--muted-foreground)';

              return (
                <div style={containerStyle}>
                  <span
                    style={{
                      fontSize: '0.75rem',
                      fontWeight: '600',
                      color: labelColor,
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                    }}
                  >
                    {label}
                  </span>
                  <div style={{ marginTop: '0.5rem' }}>
                    <InlineEditableText
                      value={value}
                      onCommit={(next) => persistLocationField(field, next)}
                      savedFieldLabel={label}
                      readOnly={!canInlineEdit}
                      disabled={savingAnalysisField}
                      allowEmpty
                      multiline
                      rows={4}
                      placeholder={label}
                      tooltip={editTooltip}
                      className="w-full font-normal text-base text-foreground leading-relaxed"
                      displayClassName="w-full"
                    />
                  </div>
                </div>
              );
            };

            return (
              <>
                <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-end">
                  <div
                    className="min-w-0 flex-1"
                    style={{ padding: '1rem', backgroundColor: 'var(--muted)', border: '1px solid var(--border)', borderRadius: '6px' }}
                  >
                    <span
                      style={{
                        fontSize: '0.75rem',
                        fontWeight: '600',
                        color: 'var(--muted-foreground)',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                      }}
                    >
                      {t('description_manual')}
                    </span>
                    <div style={{ marginTop: '0.5rem' }}>
                      <RichTextField
                        value={editNotesValue}
                        onChange={setEditNotesValue}
                        placeholder={t('location_notes_placeholder')}
                        minHeight="120px"
                        maxHeight="40vh"
                        mentionableEntities={mentionableEntities}
                        autoFocus={isModal}
                      />
                    </div>
                    <p
                      style={{
                        margin: '0.5rem 0 0 0',
                        fontSize: '0.75rem',
                        color: 'var(--muted-foreground)',
                        lineHeight: '1.4',
                      }}
                    >
                      {t('user_notes_analysis_safe_help')}
                    </p>
                  </div>
                  {linkedImagesCarousel ? (
                    <div className="mx-auto shrink-0 sm:mx-0">{linkedImagesCarousel}</div>
                  ) : null}
                </div>

                <div style={{ marginBottom: '1.5rem' }}>
                  <span
                    style={{
                      fontSize: '0.75rem',
                      fontWeight: '600',
                      color: 'var(--muted-foreground)',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                    }}
                  >
                    {t('entity_relevance')}
                  </span>
                  <div style={{ marginTop: '0.5rem' }}>
                    <EntityRelevanceDisplay value={location.relevance} size="detailed" />
                  </div>
                </div>

                {renderEditableAnalysisField('description', t('description'))}
                {renderEditableAnalysisField('narrativeFunction', t('location_narrative_function'))}
                {renderEditableAnalysisField('physicalCharacteristics', t('location_physical_characteristics'))}
                {renderEditableAnalysisField('rulesAndLogic', t('location_rules_and_logic'))}

                {locationId && !isModal && (
                  <div style={{ marginBottom: '1.5rem', padding: '1rem', backgroundColor: 'var(--muted)', border: '1px solid var(--border)', borderRadius: '6px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                      <span
                        style={{
                          fontSize: '0.75rem',
                          fontWeight: '600',
                          color: 'var(--muted-foreground)',
                          textTransform: 'uppercase',
                          letterSpacing: '0.05em',
                        }}
                      >
                        {t('analysis')}
                      </span>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        {(location.historicalCorrection || location.improvements) && (
                          <span style={{ color: '#10b981', fontSize: '1rem' }}>✓</span>
                        )}
                        <AnalyzeButton
                          onClick={() => {
                            void handleAnalyzeLocation();
                          }}
                          isAnalyzing={analyzing}
                          className="mr-0"
                          tooltip={t('analyze_location_tooltip')}
                        >
                          {analyzing
                            ? t('analyzing')
                            : location.historicalCorrection || location.improvements
                              ? t('reanalyze')
                              : t('analyze')}
                        </AnalyzeButton>
                      </div>
                    </div>
                    {!location.historicalCorrection && !location.improvements && (
                      <p style={{ margin: 0, fontSize: '0.875rem', color: 'var(--muted-foreground)' }}>
                        {t('location_no_analysis_yet')}
                      </p>
                    )}
                  </div>
                )}

                {location.historicalCorrection
                  ? renderEditableAnalysisField('historicalCorrection', t('location_historical_correction'), {
                      highlight: 'danger',
                    })
                  : null}
                {location.improvements
                  ? renderEditableAnalysisField('improvements', t('location_improvements'), {
                      highlight: 'success',
                    })
                  : null}
              </>
            );
          })()}

          {projectId && (relatedEntities.linksTo.length > 0 || relatedEntities.referencedIn.length > 0) && (
              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{ fontSize: '0.875rem', fontWeight: 600, display: 'block', marginBottom: '0.5rem' }}>
                  {t('related_entities')}
                </label>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                  {relatedEntities.linksTo.length > 0 && (
                    <div>
                      <span style={{ fontSize: '0.75rem', color: 'var(--muted-foreground)', display: 'block', marginBottom: '0.25rem' }}>
                        {t('links_to')}
                      </span>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                        {relatedEntities.linksTo.map((e) => (
                          <RelatedEntityLink
                            key={`${e.type}-${e.id}-${e.documentId ?? ''}`}
                            entity={e}
                            projectId={projectId}
                            onRemove={() => handleRemoveLinksTo(e)}
                          />
                        ))}
                      </div>
                    </div>
                  )}
                  {relatedEntities.referencedIn.length > 0 && (
                    <div>
                      <span style={{ fontSize: '0.75rem', color: 'var(--muted-foreground)', display: 'block', marginBottom: '0.25rem' }}>
                        {t('referenced_in')}
                      </span>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                        {relatedEntities.referencedIn.map((e) => (
                          <RelatedEntityLink
                            key={`${e.type}-${e.id}-${e.documentId ?? ''}`}
                            entity={e}
                            projectId={projectId}
                            onRemove={() => handleRemoveReferencedIn(e)}
                          />
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
        </div>
  );

  const renameImpactDialog = (
    <ConfirmDialog
      open={renameImpactOpen}
      onOpenChange={(open) => {
        setRenameImpactOpen(open);
        if (!open) {
          setPendingRenameName(null);
          setRenameImpactSources([]);
          if (location) {
            setEditNameValue(location.name);
          }
        }
      }}
      title={t('mention_rename_confirm_title')}
      description={t('mention_rename_confirm_description_location')
        .replace('{from}', location?.name ?? '')
        .replace('{to}', pendingRenameName ?? '')}
      confirmLabel={t('rename')}
      cancelLabel={t('cancel')}
      destructive={false}
      confirmLoading={renameImpactLoading}
      onConfirm={() => confirmLocationRenameWithMentions()}
    >
      <MentionRenameImpactList
        sources={renameImpactSources}
        projectId={projectId ?? resolvedProjectId}
      />
    </ConfirmDialog>
  );

  if (isModal) {
    return (
      <>
        <EntityDetailModalLayout header={modalHeader} footer={detailFooter}>
          {detailCard}
        </EntityDetailModalLayout>
        <ConfirmDialog
          open={deleteConfirmOpen}
          onOpenChange={setDeleteConfirmOpen}
          title={t('delete_location')}
          description={t('confirm_delete_location')}
          confirmLabel={t('delete_location')}
          onConfirm={() => {
            void deleteLocation();
          }}
        />
        {renameImpactDialog}
      </>
    );
  }

  return (
    <>
    <div
      style={{ padding: '2rem', maxWidth: '1200px', margin: '0 auto' }}
    >
      {!isModal && (
        <div style={{ marginBottom: '2rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h1 style={{ fontSize: '1.875rem', fontWeight: '600', margin: 0, color: 'var(--foreground)' }}>
            {location.name}
          </h1>
        </div>
      )}


      {detailCard}
      {detailFooter ? <div className="mt-6">{detailFooter}</div> : null}
    </div>
    <ConfirmDialog
      open={deleteConfirmOpen}
      onOpenChange={setDeleteConfirmOpen}
      title={t('delete_location')}
      description={t('confirm_delete_location')}
      confirmLabel={t('delete_location')}
      onConfirm={() => {
        void deleteLocation();
      }}
    />
    {renameImpactDialog}
    </>
  );
};

